import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { invoiceCostLKR } from '../../utils/costPax';
import { downloadCsv } from '../../utils/downloadCsv';

// Tickets Issued — every ticket since the start with its invoice, customer, passengers,
// agent, airline/PNR/route, supplier(s), nett, selling price, GP and margin. Nett/GP come
// from the linked invoice's costing (same pax-aware rule as the invoice page), so they
// match what the invoice itself shows. Void/cancelled tickets are hidden unless asked for.

const PAGE = 1000;
async function fetchAllPaged(build) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

const fmt = (n) => (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const routeOf = (t) => {
  const segs = Array.isArray(t.segments) ? t.segments.filter(s => s.route_from || s.route_to) : [];
  if (segs.length) return segs.map(s => `${s.route_from || '?'}-${s.route_to || '?'}`).join(' / ');
  return [t.route_from, t.route_to].filter(Boolean).join('-');
};
const pnrOf = (t) => {
  const segs = Array.isArray(t.segments) ? t.segments : [];
  return [...new Set([t.pnr, ...segs.map(s => s.pnr)].filter(Boolean))].join(', ');
};

export default function TicketsIssued() {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showVoid, setShowVoid] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const [ags, tks] = await Promise.all([
          fetchAllPaged(() => supabase.from('agents').select('id, tls_code, full_name').order('tls_code')),
          fetchAllPaged(() => supabase.from('tickets')
            .select('id, ticket_ref, ticket_number, airline, pnr, route_from, route_to, segments, departure_date, ticket_status, care_of_agent_id, created_at, ' +
                    'clients(full_name, care_of_agent_id), invoice:invoice_id(id, invoice_number, issued_date, status, line_items, cost_items)')
            .order('created_at', { ascending: false })),
        ]);
        setAgents(ags);
        const agentById = Object.fromEntries(ags.map(a => [a.id, a]));
        setRows(tks.map(t => {
          const inv = t.invoice;
          const sell = inv ? (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0) : 0;
          const nett = inv ? invoiceCostLKR(inv) : 0;
          const gp = sell - nett;
          const agentId = t.care_of_agent_id || t.clients?.care_of_agent_id || '';
          const a = agentById[agentId];
          const passengers = inv ? (inv.line_items || []).map(l => (l.description || '').split(' — ')[0].trim()).filter(Boolean).join('; ') : '';
          const voided = ['VOID', 'CANCELLED'].includes(inv?.status) || ['CANCELLED', 'VOID', 'REFUNDED'].includes((t.ticket_status || '').toUpperCase());
          return {
            id: t.id,
            ticket_ref: t.ticket_ref,
            ticket_number: t.ticket_number || '',
            date: inv?.issued_date || (t.created_at || '').slice(0, 10),
            invoice_id: inv?.id, invoice_number: inv?.invoice_number || '',
            customer: t.clients?.full_name || '',
            passengers,
            agent_id: agentId,
            agent: a ? `${a.tls_code} — ${a.full_name}` : '',
            airline: t.airline || '',
            pnr: pnrOf(t),
            route: routeOf(t),
            travel_date: (t.departure_date || '').slice(0, 10),
            suppliers: inv ? [...new Set((inv.cost_items || []).map(c => (c.supplier_label || '').trim()).filter(Boolean))].join(', ') : '',
            nett, sell, gp, margin: sell > 0 ? (gp / sell) * 100 : 0,
            status: inv?.status || t.ticket_status || '',
            voided,
          };
        }));
      } catch (e) {
        setError(e.message || String(e));
      }
      setLoading(false);
    })();
  }, []);

  const visible = useMemo(() => {
    const lq = q.trim().toLowerCase();
    return rows.filter(r =>
      (showVoid || !r.voided) &&
      (!agentFilter || r.agent_id === agentFilter) &&
      (!from || r.date >= from) && (!to || r.date <= to) &&
      (!lq || `${r.ticket_ref} ${r.ticket_number} ${r.invoice_number} ${r.customer} ${r.passengers} ${r.airline} ${r.pnr} ${r.route} ${r.suppliers} ${r.agent}`.toLowerCase().includes(lq)));
  }, [rows, q, agentFilter, from, to, showVoid]);

  const totals = useMemo(() => visible.reduce((t, r) => ({ nett: t.nett + r.nett, sell: t.sell + r.sell, gp: t.gp + r.gp }), { nett: 0, sell: 0, gp: 0 }), [visible]);

  const exportCsv = () => {
    const agentCode = agents.find(a => a.id === agentFilter)?.tls_code;
    downloadCsv([
      ['Ticket Ref', 'Ticket No', 'Issue Date', 'Invoice', 'Customer', 'Passengers', 'Agent', 'Airline', 'PNR', 'Route', 'Travel Date', 'Supplier(s)', 'Nett Cost (LKR)', 'Selling Price (LKR)', 'GP (LKR)', 'Margin %', 'Status'],
      ...visible.map(r => [r.ticket_ref, r.ticket_number, r.date, r.invoice_number, r.customer, r.passengers, r.agent, r.airline, r.pnr, r.route, r.travel_date, r.suppliers, r.nett.toFixed(2), r.sell.toFixed(2), r.gp.toFixed(2), r.margin.toFixed(1), r.status]),
      ['', '', '', 'TOTAL', '', '', '', '', '', '', '', '', totals.nett.toFixed(2), totals.sell.toFixed(2), totals.gp.toFixed(2), totals.sell > 0 ? (totals.gp / totals.sell * 100).toFixed(1) : '', ''],
    ], `tickets-issued-${agentCode || 'all'}${from ? `-from-${from}` : ''}${to ? `-to-${to}` : ''}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Tickets Issued</div>
          <div className="page-subtitle">Every ticket since the start — invoice, customer, agent, airline, supplier, nett, selling price and GP</div>
        </div>
        <button className="btn btn-primary" onClick={exportCsv} disabled={loading || !visible.length}>Download CSV</button>
      </div>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select className="form-input" style={{ maxWidth: 260 }} value={agentFilter} onChange={e => setAgentFilter(e.target.value)}>
          <option value="">All agents</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.tls_code} — {a.full_name}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>From <input type="date" className="form-input" style={{ width: 150, display: 'inline-block' }} value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>To <input type="date" className="form-input" style={{ width: 150, display: 'inline-block' }} value={to} onChange={e => setTo(e.target.value)} /></label>
        <input className="form-input" style={{ maxWidth: 280 }} placeholder="Search ticket, PNR, passenger, airline…" value={q} onChange={e => setQ(e.target.value)} />
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showVoid} onChange={e => setShowVoid(e.target.checked)} /> Include void / cancelled
        </label>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{visible.length} ticket{visible.length === 1 ? '' : 's'}</span>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-label">Selling Price</div><div className="stat-value amount">{fmt(totals.sell)}</div></div>
        <div className="stat-card"><div className="stat-label">Nett Cost</div><div className="stat-value amount">{fmt(totals.nett)}</div></div>
        <div className="stat-card"><div className="stat-label">GP</div><div className="stat-value amount" style={{ color: totals.gp < 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt(totals.gp)}</div></div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Date</th><th>Invoice</th><th>Customer / Passengers</th><th>Agent</th><th>Airline</th><th>PNR</th><th>Route</th><th>Supplier(s)</th>
              <th style={{ textAlign: 'right' }}>Nett</th><th style={{ textAlign: 'right' }}>Selling Price</th>
              <th style={{ textAlign: 'right' }}>GP</th><th style={{ textAlign: 'right' }}>Margin</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
            ) : !visible.length ? (
              <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No tickets</td></tr>
            ) : visible.map(r => (
              <tr key={r.id} style={{ opacity: r.voided ? 0.5 : 1 }}>
                <td style={{ fontSize: 12 }}>{r.date}</td>
                <td>{r.invoice_id ? <Link to={`/invoices/${r.invoice_id}`}>{r.invoice_number}</Link> : <span style={{ color: 'var(--text-muted)' }}>{r.ticket_ref}</span>}</td>
                <td><div>{r.customer}</div>{r.passengers && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.passengers}</div>}</td>
                <td style={{ fontSize: 12 }}>{r.agent || '—'}</td>
                <td style={{ fontSize: 12 }}>{r.airline}</td>
                <td style={{ fontSize: 12 }}>{r.pnr}</td>
                <td style={{ fontSize: 12 }}>{r.route}</td>
                <td style={{ fontSize: 12 }}>{r.suppliers || '—'}</td>
                <td style={{ textAlign: 'right' }} className="amount">{fmt(r.nett)}</td>
                <td style={{ textAlign: 'right' }} className="amount">{fmt(r.sell)}</td>
                <td style={{ textAlign: 'right', color: r.gp < 0 ? 'var(--danger)' : undefined }} className="amount">{fmt(r.gp)}</td>
                <td style={{ textAlign: 'right', fontSize: 12 }}>{r.margin.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
